import ValuationOriginEnum from '@constants/enums/valuation/origin';
import ValuationTypeEnum from '@constants/enums/valuation/type';
import ValuationDispositionEnum from '@constants/enums/valuation/disposition';
import TaxStatusEnum from '@constants/enums/taxStatus';

import i18n from '@plugins/i18n';

import FieldExchange from '@/components/fields/Exchange.vue';
import FieldUser from '@/components/fields/User.vue';
import Collection from '@/models/Collection';
import Product from '@/models/Product';
import Valuation from '@/models/Valuation';
import Adviser from '@/models/Adviser';

export class ValuationCollection extends Collection {
    static type = 'ValuationCollection';
    static endpoint = Product.api.getValuations;
    static model = Valuation;

    static fields() {
        return {
            product_id: this.string(null).nullable(),
            tax_status: this.enum(TaxStatusEnum).nullable()
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.product_id) {
            filters.product_id = this.filterOperator('is') + ':' + this.product_id;
        }

        return filters;
    }

    get headers() {
        return [
            this.createHeader('effective_at', i18n.t('effective_at'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'date'
            }),
            this.createHeader('origin', i18n.t('origin'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'enum',
                enum: ValuationOriginEnum,
                enumA11yKey: 'enums.valuation_origin'
            }),
            this.createHeader('type', i18n.t('type'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'enum',
                enum: ValuationTypeEnum,
                enumA11yKey: 'enums.valuation_type'
            }),
            this.createHeader('share_price', i18n.t('share_price'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'money',
                formatOptions: {
                    decimals: 6
                }
            }),
            this.createHeader('nav_price', i18n.t('nav_price'), {
                // active: this.tax_status === TaxStatusEnum.VCT,
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'money',
                formatOptions: {
                    decimals: 6
                }
            }),
            this.createHeader('revaluation_date', i18n.t('revaluation_date'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'date'
            }),
            this.createHeader('disposition', i18n.t('disposition'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                format: 'enum',
                enum: ValuationDispositionEnum,
                enumA11yKey: 'enums.valuation_disposition'
            }),
            this.createHeader('exchange_id', i18n.t('exchange'), {
                width: '10rem',
                filterable: true,
                sortable: false,
                path: 'exchange.name'
            }),
            this.createHeader('description', i18n.t('description'), {
                width: '18rem',
                sortable: false
            })
        ];
    }

    get authorised_by_filters() {
        if (!this.Auth().is_gi) {
            return {};
        }

        const gi = Adviser.findBy({ name: 'GrowthInvest' });

        if (!gi) {
            return {};
        }

        return {
            role_in_organisation: `${gi.id}`
        };
    }

    get bulk_update_headers() {
        return [
            ...super.bulk_update_headers,
            this.createHeader('name', i18n.t('name'), {
                active: false,
                editable: false,
                filterable: false,
                format: 'string',
                width: '100%',
                component: 'DisplayString',
                componentOptions: {
                    label: true
                }
            }),
            this.createHeader('effective_at', i18n.t('effective_at'), {
                width: '10rem',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/Date.vue')
                },
                sortable: true,
                editable: false,
                format: 'datetime',
                component: 'FieldDatetime',
                componentOptions: {
                    display: true,
                    placeholder: i18n.t('effective_at')
                }
            }),
            this.createHeader('exchange_id', i18n.t('exchange'), {
                width: '14rem',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: FieldExchange
                },
                sortable: true,
                editable: false,
                path: 'exchange.name'
            }),
            this.createHeader('origin', i18n.t('origin'), {
                width: '15rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'enum',
                enum: ValuationOriginEnum,
                enumA11yKey: 'enums.valuation_origin',
                component: 'FieldEnum',
                componentOptions: {
                    placeholder: i18n.t('origin')
                }
            }),
            this.createHeader('type', i18n.t('type'), {
                width: '14rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'enum',
                enum: ValuationTypeEnum,
                enumA11yKey: 'enums.valuation_type',
                component: 'FieldEnum',
                componentOptions: {
                    placeholder: i18n.t('type')
                }
            }),
            this.createHeader('share_price', i18n.t('share_price'), {
                width: '10rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'money',
                component: 'FieldMoney',
                componentOptions: {
                    placeholder: i18n.t('share_price'),
                    decimals: 6
                }
            }),
            this.createHeader('nav_price', i18n.t('nav_price'), {
                // active: this.tax_status === TaxStatusEnum.VCT,
                width: '10rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'money',
                component: 'FieldMoney',
                componentOptions: {
                    placeholder: i18n.t('nav_price'),
                    decimals: 6
                }
            }),
            this.createHeader('revaluation_date', i18n.t('revaluation_date'), {
                width: '11rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'date',
                component: 'FieldDate',
                componentOptions: {
                    placeholder: i18n.t('revaluation_date')
                }
            }),
            this.createHeader('disposition', i18n.t('disposition'), {
                width: '10rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'enum',
                enum: ValuationDispositionEnum,
                enumA11yKey: 'enums.valuation_disposition',
                component: 'FieldEnum',
                componentOptions: {
                    placeholder: i18n.t('disposition')
                }
            }),
            this.createHeader('description', i18n.t('description'), {
                width: '22rem',
                filterable: false,
                sortable: false,
                editable: true,
                component: 'FieldString'
            }),
            this.createHeader('authorised_date', i18n.t('authorised_date'), {
                width: '12rem',
                filterable: true,
                sortable: true,
                editable: true,
                format: 'date',
                component: 'FieldDate',
                componentOptions: {
                    placeholder: i18n.t('authorised_date')
                }
            }),
            this.createHeader('authorised_by_id', i18n.t('authorised_by'), {
                width: '14rem',
                filterable: true,
                sortable: true,
                editable: true,
                component: FieldUser,
                componentOptions: {
                    placeholder: i18n.t('authorised_by'),
                    filters: {
                        ...this.authorised_by_filters
                    }
                }
            })
        ];
    }
}

export default ValuationCollection;
